// Garment Designer. Fabric.js canvas with mockup background, layer tools
// (text + image upload), product/colour/size pickers, explicit Save
// button (dirty flag → PATCH the design's spec on the API), and a submit
// flow that creates a pending_payment order with the proof PNG attached
// as a signed-URL file.
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { fabric } from 'fabric'
import TopBar from '../components/TopBar'
import LibraryDrawer, { SavedDesign, SavedUpload } from '../components/LibraryDrawer'
import TextToolbar from '../components/TextToolbar'
import AlignmentToolbar from '../components/AlignmentToolbar'
import type { InlineUploadLibraryHandle } from '../components/InlineUploadLibrary'
import StudioMediaPicker, { StudioAsset } from '../components/StudioMediaPicker'
import UploadConsentModal, { PendingUpload, computeUploadWarnings, hasSessionConsent, rememberSessionConsent } from '../components/UploadConsentModal'
import PreviewModal from '../components/PreviewModal'
import ThreeDPreviewModal from '../components/ThreeDPreviewModal'
import { apiFetch, useApiSrc } from '../lib/api'
import { calibrationCss as mockupCalibrationCss } from './admin/PrintAreaEditor'
import DesignerWizardModals from './designer/DesignerWizardModals'
import SubmitOrderModal from './designer/SubmitOrderModal'
import AddDesignModal, { GeneratedFile } from '../components/AddDesignModal'
import AddDesignChoiceModal from '../components/AddDesignChoiceModal'
import AutomatedDesignWizard, { type AutomatedDesignResult, type FrontPosition, type ManualChoiceResult } from '../components/AutomatedDesignWizard'

import type { CatColour, PrintLocation, CatProduct, Catalog } from '../lib/catalog-types'
import { silhouettePath, locationPrintWidthMm, canvasHasMedia, garmentTotals, unionBox, centerDelta, fitPlacement, PRINT_DEFAULT, CANVAS_W, CANVAS_H, type Tool, type Box } from './designer/helpers'
import DesignerToolNav from './designer/DesignerToolNav'
import DesignerLocations from './designer/DesignerLocations'
import DesignerToolbar from './designer/DesignerToolbar'
import { useDesignerSubmit } from './designer/useDesignerSubmit'
import { useCanvasHistory } from './designer/useCanvasHistory'
import { useLocationThumbs } from './designer/useLocationThumbs'
import { useTextDragCreate } from './designer/useTextDragCreate'
import DesignerSidebar from './designer/DesignerSidebar'
interface DesignRow { id: string; kind: string; name: string | null; status: string }
interface FileResp { id: string; url: string }



export default function Designer() {
  const navigate = useNavigate()
  const { user, getAccessToken } = useAuth()
  const apiSrc = useApiSrc()

  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [productSlug, setProductSlug] = useState<string>('tee')
  const [colourId, setColourId] = useState<string | null>(null)
  const [qty, setQty] = useState<Record<string, number>>({})

  const [designId, setDesignId] = useState<string | null>(null)
  const [designName, setDesignName] = useState<string | null>(null)
  const [saveNameOpen, setSaveNameOpen] = useState(false)
  const [saveNameValue, setSaveNameValue] = useState('')
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  // Dirty flag: true when there are unsaved canvas changes since the last
  // explicit Save. Auto-save was removed because each tick created a new
  // library row, so the user's library piled up dozens of identical drafts
  // while they edited. Saves are now explicit (Save button) — same library
  // wire underneath, just one row per click instead of one per nudge.
  const [dirty, setDirty] = useState(false)
  const markDirty = useCallback(() => { setDirty(true); setSaving('idle') }, [])

  const [tool, setTool] = useState<Tool>('product')
  // Wizard-modal state — when a step pill in the EditorWizardBar is
  // clicked, this opens a focused popup with the inputs for that step.
  // Null means no modal is open.
  const [wizModalStep, setWizModalStep] = useState<string | null>(null)
  // Add-Design popup state — opens from the left-nav "Add Design" button
  // AND from the wizard's "Add artwork" step. Two tabs inside: Upload
  // (file picker + drag-drop) and Generate with AI (Nano Banana 2).
  const [addDesignOpen, setAddDesignOpen] = useState(false)
  // Choice modal — shown first when "Add Design" is triggered. The
  // customer picks Automated (guided wizard) or Manual (existing modal).
  const [designChoiceOpen, setDesignChoiceOpen] = useState(false)
  const [autoWizardOpen, setAutoWizardOpen] = useState(false)
  const [wizardResetKey, setWizardResetKey] = useState(0)
  const [wizardProgress, setWizardProgress] = useState<{ current: number; total: number; stepName: string } | null>(null)
  const handleWizardStepChange = useCallback((current: number, total: number, stepName: string) => {
    setWizardProgress({ current, total, stepName })
  }, [])

  const canvasElRef = useRef<HTMLCanvasElement | null>(null)
  const fabricRef = useRef<fabric.Canvas | null>(null)
  const printGuideRef = useRef<fabric.Rect | null>(null)
  const printLabelRef = useRef<fabric.Text | null>(null)

  const [hasSelection, setHasSelection] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [studioMediaOpen, setStudioMediaOpen] = useState(false)
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null)
  const [pendingWarnings, setPendingWarnings] = useState<string[]>([])
  const [mockupErrored, setMockupErrored] = useState<Record<string, boolean>>({})
  const [canvasTick, setCanvasTick] = useState(0) // bumps on every render — TextToolbar listens
  const bump = () => setCanvasTick((x) => x + 1)

  // Multi-location state: which print area the user is currently designing,
  // and a per-location snapshot of Fabric JSON. Switching locations snapshots
  // the current canvas, then loads the target's saved state.
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null)
  // Customer-side visibility: Front is shown by default, all other admin-
  // configured locations (Back, Sleeves, Hem, etc.) are tucked behind an
  // "Add print location" expander and only revealed once the customer opts
  // in. Keeps the initial canvas focused on the most-used location.
  const [revealedLocationIds, setRevealedLocationIds] = useState<Set<string>>(() => new Set())
  const locationStatesRef = useRef<Record<string, object>>({})
  // Per-piece flat-PNG thumbnails uploaded at submit time. Keyed by piece
  // key ('location:<locId>') -> file id. Merged into spec via buildState
  // so the API can persist them on the design.
  const pieceThumbnailsRef = useRef<Record<string, string>>({})
  // Mirror product + active location into refs so the Fabric event handlers
  // (bound once on canvas init) always see the latest values when the user
  // switches locations or the catalog finishes loading.
  const productRef = useRef<{
    printArea?: { left: number; top: number; width: number; height: number } | null
    printLocations?: PrintLocation[]
  } | null>(null)
  const activeLocationIdRef = useRef<string | null>(null)
  // Placement timer IDs — cleared at the start of each handleAutomatedComplete
  // call so re-running the wizard within 800 ms cancels the prior chain.
  const placementTimersRef = useRef<number[]>([])
  // Live selection readout — size in mm + effective DPI for the currently
  // selected image, recomputed on every object:scaling event.
  const [selection, setSelection] = useState<{ widthMm: number; heightMm: number; ppi: number } | null>(null)
  // Per-location thumbnail data URLs. Filled when a location switch or
  // object:modified happens and the location has user content. Used by the
  // sidebar buttons + the "+ Add print location" tray to surface what's been
  // placed on each location at a glance.
  const { locationThumbs, setLocationThumbs, captureLocationThumb } = useLocationThumbs({ fabricRef, printGuideRef, printLabelRef })
  // Canvas zoom + history (undo/redo) + border visibility.
  const [zoom, setZoom] = useState(1)
  const mainRef = useRef<HTMLElement>(null)
  const [borderOn, setBorderOn] = useState(true)
  // Preview modal — opens on button press, sends the composited canvas to
  // Fal for a realistic image-to-image render.
  const [previewOpen, setPreviewOpen] = useState(false)
  // 3D preview modal — renders the live canvas onto a curved garment panel
  // in a Three.js scene. Pure client-side render, no API call, works on
  // every product (no AI feature flag, no admin opt-in required).
  const [threeDOpen, setThreeDOpen] = useState(false)
  // Ref mirror of borderOn so non-React code paths (loadFromJSON callbacks,
  // location switching) can read the current value without re-binding.
  const switchingLocationRef = useRef(false)
  const libraryRef = useRef<InlineUploadLibraryHandle | null>(null)

  // proofs[] carries one composited preview per location with media so the
  // customer can confirm every printed side before paying. proof + proofFileId
  // remain populated with the active location for backward compatibility with
  // the existing order/proof flow.
  // Submit-modal state + the openSubmit/placeOrder flow now live in
  // useDesignerSubmit (wired up below, once its deps exist).
  // Soft validation cue. Flipped true when Submit is pressed without any
  // size + qty selected; flashes a highlighted message above the Sizes
  // panel and auto-clears once the customer enters a quantity.
  const [needsSizes, setNeedsSizes] = useState(false)
  const sizesRef = useRef<HTMLDivElement | null>(null)
  const [shipping, setShipping] = useState({
    fullName: '', email: user?.email ?? '', phone: '', line1: '', line2: '',
    city: '', region: '', postcode: '', country: 'AU',
  })
  // Pre-populate from the signed-in customer's saved shipping (or, if no
  // explicit profile row, the last order's shipping snapshot — /api/me
  // does that fallback for us). Hits /api/me directly so the prefill
  // works even when AuthKit React's `user` hasn't hydrated yet but the
  // server-side sealed cookie is valid. Runs once on mount; only fills
  // empty fields so a customer who's already typed a new address isn't
  // reset on a stale fetch.
  const shippingHydrated = useRef(false)
  useEffect(() => {
    if (shippingHydrated.current) return
    let cancelled = false
    ;(async () => {
      try {
        type MeResp = {
          authenticated: boolean
          user?: { email: string } | null
          shipping?: { fullName: string; phone: string | null; line1: string; line2: string | null; city: string; region: string | null; postcode: string; country: string } | null
        }
        const m = await apiFetch<MeResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/me')
        if (cancelled || !m?.authenticated) return
        shippingHydrated.current = true
        const s = m.shipping
        const userEmail = m.user?.email ?? ''
        setShipping((prev) => ({
          fullName: prev.fullName || s?.fullName || '',
          email:    prev.email    || userEmail,
          phone:    prev.phone    || (s?.phone ?? ''),
          line1:    prev.line1    || s?.line1 || '',
          line2:    prev.line2    || (s?.line2 ?? ''),
          city:     prev.city     || s?.city || '',
          region:   prev.region   || (s?.region ?? ''),
          postcode: prev.postcode || s?.postcode || '',
          country:  prev.country  || s?.country || 'AU',
        }))
      } catch { /* signed out / no profile / network — keep blanks */ }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load catalog once
  useEffect(() => {
    (async () => {
      try {
        const c = await apiFetch<Catalog>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/catalog')
        setCatalog(c)
        const garments = c.products.filter((p) => p.kind === 'garment')
        if (garments.length && !garments.find((p) => p.slug === productSlug)) setProductSlug(garments[0]!.slug)
      } catch (e) { setError((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const product = useMemo(() => catalog?.products.find((p) => p.slug === productSlug && p.kind === 'garment'), [catalog, productSlug])
  const colour = useMemo(() => product?.colours.find((c) => c.id === colourId) ?? product?.colours[0], [product, colourId])

  // Mockup fallback chain: side×colour → colour-only → location-only → garment default → any sideColour.
  // The final "any sideColour" tier handles the initial render before activeLocationId is set.
  const resolvedMockupUrl = useMemo(() => {
    if (!product) return null
    const locId = activeLocationId
    const cId = colour?.id ?? null
    if (locId && cId) {
      const sc = product.sideColourMockups?.find((m) => m.locationId === locId && m.colourId === cId)
      if (sc) return sc.url
    }
    if (cId) {
      const colMockup = product.colours.find((c) => c.id === cId)?.mockupUrl
      if (colMockup) return colMockup
    }
    const activeLocation = product.printLocations?.find((l) => l.id === locId)
    if (activeLocation?.mockupUrl) return activeLocation.mockupUrl
    if (product.mockupUrl) return product.mockupUrl
    // Final fallback: any sideColourMockup matching the current colour, then any available.
    // Prevents SVG flash on first render when activeLocationId hasn't been initialised yet.
    const scByColour = cId ? product.sideColourMockups?.find((m) => m.colourId === cId) : null
    return scByColour?.url ?? product.sideColourMockups?.[0]?.url ?? null
  }, [product, activeLocationId, colour])

  useEffect(() => { if (product && !colourId && product.colours[0]) setColourId(product.colours[0].id) }, [product, colourId])

  // Init Fabric canvas once
  useEffect(() => {
    if (!canvasElRef.current || fabricRef.current) return
    const c = new fabric.Canvas(canvasElRef.current, {
      width: CANVAS_W, height: CANVAS_H,
      selection: true, preserveObjectStacking: true,
      backgroundColor: 'transparent',
    })
    // Initial guide uses current product's printArea if available, else default.
    // A separate effect (below) keeps it in sync when the product changes.
    const initArea = catalog?.products.find((p) => p.slug === productSlug)?.printArea ?? PRINT_DEFAULT
    const guide = new fabric.Rect({
      left: initArea.left, top: initArea.top, width: initArea.width, height: initArea.height,
      fill: 'transparent', stroke: '#C8A96E', strokeDashArray: [6, 6], strokeWidth: 1.2,
      selectable: false, evented: false, hoverCursor: 'default',
    })
    const label = new fabric.Text('PRINT AREA', {
      left: initArea.left + 4, top: initArea.top - 14, fontSize: 9, fontFamily: 'Inter',
      fill: '#C8A96E', selectable: false, evented: false, hoverCursor: 'default',
    })
    c.add(guide); c.add(label)
    fabricRef.current = c; printGuideRef.current = guide; printLabelRef.current = label
    // Auto-fit initial zoom here so the canvas exists when we apply it.
    if (mainRef.current) {
      const el = mainRef.current
      const fitZoom = Math.min(el.clientWidth / CANVAS_W, el.clientHeight / CANVAS_H) * 0.92
      setZoom(Math.max(0.4, fitZoom))
    }
    // Update both the selection-exists flag AND the live size/DPI readout
    // for image objects. Recomputed on every selection event AND on every
    // object:scaling tick so the customer sees the chip move as they drag.
    const recomputeSelection = () => {
      const a = c.getActiveObject()
      const isUserObj = !!a && a !== guide && a !== label
      setHasSelection(isUserObj)
      if (!isUserObj || !a) { setSelection(null); return }
      if (a.type === 'image') {
        const img = a as fabric.Image
        const el = img.getElement() as HTMLImageElement
        const naturalW = el?.naturalWidth ?? 0
        // Use the active location's printArea (or product default) to derive
        // the canvas-px → mm scale. Same heuristic used everywhere. Pull
        // from refs so the closure always sees the latest catalog values.
        const liveProd = productRef.current
        const liveLoc = liveProd?.printLocations?.find((l) => l.id === activeLocationIdRef.current)
        const printZone = liveLoc ?? liveProd?.printArea ?? PRINT_DEFAULT
        const printName = liveLoc?.name ?? 'Front'
        const realMm = locationPrintWidthMm(printName)
        const mmPerPx = printZone.width > 0 ? realMm / printZone.width : 0
        const widthMm  = img.getScaledWidth()  * mmPerPx
        const heightMm = img.getScaledHeight() * mmPerPx
        const ppi = naturalW > 0 && widthMm > 0 ? (naturalW / (widthMm / 25.4)) : 0
        setSelection({ widthMm: Math.round(widthMm), heightMm: Math.round(heightMm), ppi: Math.round(ppi) })
      } else {
        setSelection(null)
      }
    }
    const onSel = () => { recomputeSelection(); bump() }
    c.on('selection:created', onSel)
    c.on('selection:updated', onSel)
    c.on('selection:cleared', () => { setHasSelection(false); setSelection(null); bump() })
    c.on('object:modified',  () => {
      snapshotHistory(); markDirty(); recomputeSelection(); bump()
      // Refresh the active location's thumbnail whenever an object lands.
      if (activeLocationIdRef.current) captureLocationThumb(activeLocationIdRef.current)
    })
    c.on('object:scaling',   () => { recomputeSelection(); bump() })
    c.on('object:moving',    () => bump())
    c.on('object:added',     () => { snapshotHistory(); markDirty(); bump() })
    c.on('object:removed',   () => { snapshotHistory(); markDirty(); bump() })
    return () => { c.dispose(); fabricRef.current = null }
    // Depend on `catalog` so we re-try init once the loading-state render swaps
    // out and the <canvas> element finally enters the DOM. The guard above
    // (`if (... || fabricRef.current) return`) keeps it idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog])

  // Default the active location to "Front" (case-insensitive name match) when
  // product loads, falling back to the first configured location. Reveal only
  // that one to start — other locations (Back, Sleeves, Hem, etc.) live in
  // the "+ Add print location" tray until the customer opts into them.
  useEffect(() => {
    if (!product) return
    const locs = product.printLocations ?? []
    if (!locs.length) { setActiveLocationId(null); setRevealedLocationIds(new Set()); return }
    const front = locs.find((l) => l.name.trim().toLowerCase() === 'front') ?? locs[0]!
    if (!activeLocationId || !locs.find((l) => l.id === activeLocationId)) {
      setActiveLocationId(front.id)
    }
    // Initialise revealed set on first product load with Front (or the
    // fallback first location). Preserve any later customer reveals as the
    // product reference stays the same.
    setRevealedLocationIds((prev) => prev.size === 0 ? new Set([front.id]) : prev)
  }, [product, activeLocationId])

  const activeLocation = useMemo(() => {
    if (!product) return null
    return product.printLocations?.find((l) => l.id === activeLocationId) ?? null
  }, [product, activeLocationId])

  // Keep refs in sync so the Fabric event handlers (bound on canvas init)
  // always read the latest product + active location id.
  useEffect(() => { productRef.current = product ?? null }, [product])
  useEffect(() => { activeLocationIdRef.current = activeLocationId }, [activeLocationId])

  // Re-position the print-area guide whenever the active LOCATION (or border
  // toggle / preview mode) changes. Falls back to product.printArea (legacy)
  // → default. In preview-as-worn mode the guide is force-hidden regardless
  // of the border toggle.
  useEffect(() => {
    const c = fabricRef.current
    const guide = printGuideRef.current
    const label = printLabelRef.current
    if (!c || !guide || !label) return
    const area = activeLocation ?? product?.printArea ?? PRINT_DEFAULT
    guide.set({ left: area.left, top: area.top, width: area.width, height: area.height, visible: borderOn })
    label.set({ left: area.left + 4, top: area.top - 14, visible: borderOn })
    guide.setCoords(); label.setCoords()
    // Also make sure the guide is on the canvas — location switches / undo /
    // redo can drop it. contains() avoids double-add.
    if (!c.contains(guide)) c.add(guide)
    if (!c.contains(label)) c.add(label)
    c.requestRenderAll()
  }, [activeLocation, product, borderOn])

  // Apply zoom to the Fabric canvas.
  useEffect(() => {
    const c = fabricRef.current; if (!c) return
    c.setZoom(zoom)
    c.setWidth(CANVAS_W * zoom); c.setHeight(CANVAS_H * zoom)
    c.requestRenderAll()
  }, [zoom])


  // Strip the print-area guide rect + label from a Fabric JSON snapshot.
  // Keeping them in the snapshot is what produces duplicated, selectable
  // guides after a location switch / undo (the round-trip loses the
  // `selectable: false` flag, then our ref gets re-added on top → two guides).
  const stripGuidesFromJSON = useCallback((json: { objects?: Array<{ type?: string; strokeDashArray?: unknown; text?: string }> }) => {
    json.objects = (json.objects ?? []).filter((o) =>
      !(o.type === 'rect' && Array.isArray(o.strokeDashArray)) &&
      !(o.type === 'text' && o.text === 'PRINT AREA'))
    return json
  }, [])

  // Remove any stray guide rects/labels that loadFromJSON might have brought
  // back into the canvas — only the ref'd guide (which is non-selectable) is
  // ever allowed to live on the canvas.
  const purgeStrayGuides = useCallback(() => {
    const c = fabricRef.current; if (!c) return
    c.getObjects().slice().forEach((o) => {
      if (o === printGuideRef.current || o === printLabelRef.current) return
      const r = o as fabric.Object & { strokeDashArray?: unknown; text?: string }
      if ((o.type === 'rect' && Array.isArray(r.strokeDashArray)) || (o.type === 'text' && r.text === 'PRINT AREA')) {
        c.remove(o)
      }
    })
  }, [])

  const { undo, redo, snapshotHistory } = useCanvasHistory({
    fabricRef, switchingLocationRef, purgeStrayGuides, printGuideRef, printLabelRef, stripGuidesFromJSON,
  })

  // Switch active location — snapshot current canvas to old location, load
  // target's saved JSON. Same pattern as DTF's sheet switcher.

  const switchLocation = useCallback((nextId: string) => {
    const c = fabricRef.current; if (!c || nextId === activeLocationId) return
    switchingLocationRef.current = true
    // Snapshot current — strip guides so they don't get re-hydrated as
    // selectable objects on the way back in. Refresh the location thumbnail
    // before swapping so the sidebar shows the latest content for both the
    // previous and the new active locations.
    if (activeLocationId) {
      const json = stripGuidesFromJSON(c.toJSON() as { objects?: Array<{ type?: string; strokeDashArray?: unknown; text?: string }> })
      locationStatesRef.current[activeLocationId] = json
      captureLocationThumb(activeLocationId)
    }
    // Load target
    const target = locationStatesRef.current[nextId]
    setActiveLocationId(nextId)
    if (target) {
      c.loadFromJSON(target, () => {
        // Belt + braces — strip anything that snuck through from older saves.
        purgeStrayGuides()
        // Re-add the official (non-selectable) guide
        if (printGuideRef.current && !c.contains(printGuideRef.current)) c.add(printGuideRef.current)
        if (printLabelRef.current && !c.contains(printLabelRef.current)) c.add(printLabelRef.current)
        c.renderAll()
        window.setTimeout(() => { switchingLocationRef.current = false }, 60)
      })
    } else {
      // Empty target — clear non-guide objects only
      const guide = printGuideRef.current; const label = printLabelRef.current
      c.getObjects().slice().forEach((o) => { if (o !== guide && o !== label) c.remove(o) })
      c.renderAll()
      window.setTimeout(() => { switchingLocationRef.current = false }, 60)
    }
  }, [activeLocationId])

  // ── Canvas toolbar actions ────────────────────────────────────────────
  function selectAll() {
    const c = fabricRef.current; if (!c) return
    const guide = printGuideRef.current; const label = printLabelRef.current
    const objs = c.getObjects().filter((o) => o !== guide && o !== label)
    if (!objs.length) return
    const sel = new fabric.ActiveSelection(objs, { canvas: c })
    c.setActiveObject(sel)
    c.requestRenderAll()
  }
  function groupSelected() {
    const c = fabricRef.current; if (!c) return
    const a = c.getActiveObject()
    if (!a) return
    if (a.type === 'activeSelection') {
      const grp = (a as fabric.ActiveSelection).toGroup()
      c.setActiveObject(grp); c.requestRenderAll()
    } else if (a.type === 'group') {
      const items = (a as fabric.Group).toActiveSelection()
      c.setActiveObject(items); c.requestRenderAll()
    }
  }

  const buildState = useCallback(() => {
    const c = fabricRef.current
    if (!c) return null
    // Snapshot the live canvas into locationStatesRef so the active location's
    // current state is part of the multi-location map below.
    const live = stripGuidesFromJSON(c.toJSON() as { objects?: Array<{ type?: string; strokeDashArray?: unknown; text?: string }> })
    if (activeLocationIdRef.current) {
      locationStatesRef.current[activeLocationIdRef.current] = live
    }
    // Build a locations map keyed by locationId, filtering out anything that
    // has no real user objects. Empty buckets used to land in the saved spec
    // and confuse the admin order-detail page; only the locations the
    // customer actually placed media on go through to production.
    const locations: Record<string, { canvas: object }> = {}
    for (const [locId, snap] of Object.entries(locationStatesRef.current)) {
      if (canvasHasMedia(snap as { objects?: Array<{ type?: string; text?: string; strokeDashArray?: unknown }> })) {
        locations[locId] = { canvas: snap }
      }
    }
    // Legacy single-canvas field: prefer the active location's content if
    // any, otherwise the first non-empty location, otherwise the raw live
    // canvas (empty designs still need a saveable spec so the customer can
    // come back and finish later).
    const activeSnap = activeLocationIdRef.current ? locationStatesRef.current[activeLocationIdRef.current] : null
    const legacyCanvas = canvasHasMedia(activeSnap as { objects?: Array<{ type?: string }> })
      ? activeSnap
      : (Object.values(locations)[0]?.canvas ?? live)
    return {
      canvas: legacyCanvas, productSlug, colourId, qty,
      activeLocationId: activeLocationIdRef.current, locations,
      // Per-piece thumbnails (location:<locId> -> fileId). Populated by
      // openSubmit's flat-PNG upload pass; empty when the design is just
      // a draft.
      pieceThumbnails: { ...pieceThumbnailsRef.current },
    }
  }, [productSlug, colourId, qty, stripGuidesFromJSON])

  const persist = useCallback(async (name?: string) => {
    const spec = buildState(); if (!spec) return
    setSaving('saving')
    try {
      if (!designId) {
        const row = await apiFetch<DesignRow>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/designs', {
          method: 'POST', body: JSON.stringify({ kind: 'garment', spec, name: name ?? null }),
        })
        setDesignId(row.id)
        if (name) setDesignName(name)
      } else {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/designs/${designId}`, {
          method: 'PATCH', body: JSON.stringify({ spec }),
        })
      }
      setSaving('saved'); setDirty(false)
    } catch { setSaving('error') }
  }, [buildState, designId, getAccessToken])

  // Mark dirty whenever pickers change too — the saved spec includes
  // productSlug/colourId/qty, so any of those drifting needs an explicit save.
  useEffect(() => { markDirty() /* eslint-disable-next-line */ }, [productSlug, colourId, qty])

  // Quick-add text: drops a Textbox at the ACTIVE LOCATION's print-area
  // top-left (falling back to the legacy product.printArea when no per-
  // location config exists). Textbox (not IText) so the in-toolbar L/C/R
  // alignment buttons actually do something — they wrap text inside the
  // box width.
  function addText() {
    const c = fabricRef.current; if (!c) return
    const zone = activeLocation ?? product?.printArea ?? PRINT_DEFAULT
    const left = zone.left + 20
    const top  = zone.top  + 40
    const width = zone.width - 40
    const text = new fabric.Textbox('Your Text', {
      left, top, width: Math.max(80, width),
      fontFamily: 'Barlow Condensed', fontSize: 56, fontWeight: 'bold',
      fill: colour?.hex && colour.hex.toLowerCase() === '#0f1117' ? '#ffffff' : '#0F1117',
      textAlign: 'left',
    })
    c.add(text); c.setActiveObject(text); c.requestRenderAll()
  }

  // ─── Placement helpers — used by the wizard's "Artwork placement"
  // step. All operate on USER objects only (skip the mockup background,
  // the dashed print-area guide rect, and its PRINT AREA label) and on
  // the ACTIVE print location. Without the guide/label filter, their
  // bounding rect equals the print area exactly → user bbox == print
  // area → centerOnPrintArea shifts by (0,0) and fit scales by 1.0, so
  // the buttons would (silently) do nothing. Match by ref identity the
  // same way recomputeSelection does so we stay in sync if the tags
  // change in future.
  function userObjects(): fabric.Object[] {
    const c = fabricRef.current
    if (!c) return []
    const guide = printGuideRef.current
    const label = printLabelRef.current
    return c.getObjects().filter((o) => {
      if (o === guide || o === label) return false
      if ((o as fabric.Object & { __mockupBg?: boolean }).__mockupBg) return false
      return true
    })
  }
  function userBoundingBox(): Box | null {
    return unionBox(userObjects().map((o) => o.getBoundingRect(true, true)))
  }
  function currentPrintZone() {
    return activeLocation ?? product?.printArea ?? PRINT_DEFAULT
  }
  function placementCenter() {
    const c = fabricRef.current; if (!c) return
    const bbox = userBoundingBox(); if (!bbox) return
    const { dx, dy } = centerDelta(currentPrintZone(), bbox)
    for (const o of userObjects()) {
      o.set({ left: (o.left ?? 0) + dx, top: (o.top ?? 0) + dy })
      o.setCoords()
    }
    c.requestRenderAll(); markDirty()
  }
  function placementScale(factor: number) {
    const c = fabricRef.current; if (!c) return
    const bbox = userBoundingBox(); if (!bbox) return
    const cx = bbox.left + bbox.width / 2
    const cy = bbox.top  + bbox.height / 2
    for (const o of userObjects()) {
      o.set({
        scaleX: (o.scaleX ?? 1) * factor,
        scaleY: (o.scaleY ?? 1) * factor,
        left: cx + ((o.left ?? 0) - cx) * factor,
        top:  cy + ((o.top  ?? 0) - cy) * factor,
      })
      o.setCoords()
    }
    c.requestRenderAll(); markDirty()
  }
  function placementFit(mode: 'width' | 'height' | 'contain') {
    const c = fabricRef.current; if (!c) return
    const bbox = userBoundingBox(); if (!bbox) return
    const { factor, targetLeft, targetTop } = fitPlacement(mode, currentPrintZone(), bbox)
    for (const o of userObjects()) {
      const relX = (o.left ?? 0) - bbox.left
      const relY = (o.top  ?? 0) - bbox.top
      o.set({
        scaleX: (o.scaleX ?? 1) * factor,
        scaleY: (o.scaleY ?? 1) * factor,
        left: targetLeft + relX * factor,
        top:  targetTop  + relY * factor,
      })
      o.setCoords()
    }
    c.requestRenderAll(); markDirty()
  }

  // Drag-to-create text area — owns the textDragMode flag + canvas wiring.
  const { textDragMode, setTextDragMode } = useTextDragCreate({ fabricRef, colourHex: colour?.hex })

  // Stage 1 — file picker fires. Read file, probe natural dimensions, and
  // either gate behind the consent/warnings modal or proceed straight to
  // upload if the customer already consented this session AND the file passes.
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return
    e.target.value = ''
    if (f.size > 10 * 1024 * 1024) { alert('Max 10 MB'); return }
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      const probe = new Image()
      probe.onload = () => {
        const pending: PendingUpload = { file: f, dataUrl, naturalWidth: probe.naturalWidth, naturalHeight: probe.naturalHeight }
        const warnings = computeUploadWarnings(pending)
        // Skip the modal only when the customer already consented this
        // session AND the file is decent. Any warnings re-trigger the modal.
        if (hasSessionConsent() && warnings.length === 0) {
          void doUpload(pending)
        } else {
          setPendingUpload(pending); setPendingWarnings(warnings)
        }
      }
      probe.src = dataUrl
    }
    reader.readAsDataURL(f)
  }

  // Stage 2 — runs after the consent modal confirms (or directly when the
  // session-consent fast-path skips it).
  async function doUpload(p: PendingUpload) {
    const c = fabricRef.current; if (!c) return
    fabric.Image.fromURL(p.dataUrl, (img) => {
      img.scaleToWidth(160)
      // Drop the upload into the ACTIVE location's print zone (falls back to
      // the legacy product printArea if the product has no multi-location
      // setup). Earlier rev landed every upload at product.printArea, so an
      // upload made while designing on Back went off-zone (into the Front
      // coords) and didn't show up in the proof's Back-side render.
      const zone = activeLocation ?? product?.printArea ?? PRINT_DEFAULT
      img.set({ left: zone.left + 20, top: zone.top + 30 })
      ;(img as fabric.Object & { data?: { tempLocal?: boolean; fileId?: string } }).data = { tempLocal: true }
      c.add(img); c.setActiveObject(img); c.requestRenderAll()
    })
    try {
      const up = await apiFetch<FileResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
        method: 'POST',
        body: JSON.stringify({ originalName: p.file.name, mimeType: p.file.type || 'application/octet-stream', base64: p.dataUrl, kind: 'artwork', widthPx: p.naturalWidth, heightPx: p.naturalHeight }),
      })
      const active = c.getActiveObject() as (fabric.Image & { data?: { tempLocal?: boolean; fileId?: string } }) | null
      if (active && active.type === 'image' && active.data?.tempLocal) {
        active.setSrc(up.url, () => {
          active.data = { tempLocal: false, fileId: up.id }
          c.requestRenderAll(); markDirty()
        })
      }
      libraryRef.current?.addUpload({
        id: up.id, originalName: p.file.name, url: up.url,
        widthPx: p.naturalWidth, heightPx: p.naturalHeight,
        createdAt: new Date().toISOString(),
      })
    } catch (err) { console.warn('upload failed:', (err as Error).message) }
  }

  // Reopen a previously saved design (kind='garment'). Loads the spec into
  // the Fabric canvas + restores product / colour / qty selections.
  async function openSavedDesign(d: SavedDesign) {
    const c = fabricRef.current; if (!c) return
    try {
      const row = await apiFetch<{ item: { id: string; spec: unknown } }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/library/designs/${d.id}`)
      const spec = row.item.spec as { canvas?: object; productSlug?: string; colourId?: string; qty?: Record<string, number> } | null
      if (!spec?.canvas) return
      // Restore meta first so the mockup background swaps correctly.
      if (spec.productSlug) setProductSlug(spec.productSlug)
      if (spec.colourId) setColourId(spec.colourId)
      if (spec.qty) setQty(spec.qty)
      setDesignId(d.id)
      setDesignName(d.name ?? null)
      // Clear current objects then load the saved JSON. Re-add the guide rect
      // after the load — Fabric clears EVERYTHING including our guide artifacts.
      c.loadFromJSON(spec.canvas, () => {
        // Strip any stray guide rects from designs saved before guides were
        // properly filtered out of buildState() — defensive.
        purgeStrayGuides()
        if (printGuideRef.current) c.add(printGuideRef.current)
        if (printLabelRef.current) c.add(printLabelRef.current)
        c.requestRenderAll()
      })
      setLibraryOpen(false)
    } catch (e) { alert((e as Error).message) }
  }

  // Drop an image from the library onto the canvas without re-uploading.
  // guardLocId: if provided, the fromURL callback aborts if the active location
  // has changed by the time the image loads — prevents a slow/late-firing async
  // load from depositing artwork onto the wrong location's canvas.
  // Zone is read inside each callback from refs (not captured at call time) so
  // it reflects the location that is actually active when the image lands.
  function useUpload(u: SavedUpload, guardLocId?: string) {
    if (!fabricRef.current) return
    const tag = (o: fabric.Object) => {
      ;(o as fabric.Object & { data?: { fileId?: string; reused?: boolean } }).data = { fileId: u.id, reused: true }
    }
    if (u.mimeType === 'image/svg+xml') {
      fabric.loadSVGFromURL(u.url, (objects, options) => {
        const cv = fabricRef.current; if (!cv) return
        if (guardLocId && activeLocationIdRef.current !== guardLocId) return
        const liveZone = productRef.current?.printLocations?.find((l) => l.id === activeLocationIdRef.current) ?? productRef.current?.printArea ?? PRINT_DEFAULT
        const svg = fabric.util.groupSVGElements(objects, options)
        svg.scaleToWidth(160)
        svg.set({ left: liveZone.left + 20, top: liveZone.top + 30 })
        tag(svg)
        cv.add(svg); cv.setActiveObject(svg); cv.requestRenderAll()
      })
    } else {
      fabric.Image.fromURL(u.url, (img) => {
        const cv = fabricRef.current; if (!cv) return
        if (guardLocId && activeLocationIdRef.current !== guardLocId) return
        const liveZone = productRef.current?.printLocations?.find((l) => l.id === activeLocationIdRef.current) ?? productRef.current?.printArea ?? PRINT_DEFAULT
        img.scaleToWidth(160)
        img.set({ left: liveZone.left + 20, top: liveZone.top + 30 })
        tag(img)
        cv.add(img); cv.setActiveObject(img); cv.requestRenderAll()
      }, { crossOrigin: 'anonymous' })
    }
    setLibraryOpen(false)
  }

  function deleteSelected() {
    const c = fabricRef.current; if (!c) return
    const a = c.getActiveObject()
    if (!a || a === printGuideRef.current || a === printLabelRef.current) return
    // Multi-select: c.remove(activeSelection) just discards the wrapper but
    // leaves every contained object on the canvas. Pull each contained
    // object out and remove them individually so a multi-select Delete
    // actually deletes.
    if (a.type === 'activeSelection') {
      const grp = a as fabric.ActiveSelection
      const items = grp.getObjects().slice()
      grp.remove(...items)
      c.discardActiveObject()
      items.forEach((o) => c.remove(o))
    } else {
      c.remove(a)
    }
    c.requestRenderAll(); setHasSelection(false); setSelection(null)
  }

  const totals = useMemo(() => garmentTotals(product, qty), [product, qty])

  const { submit, setSubmit, openSubmit, placeOrder } = useDesignerSubmit({
    product, totals, colourId, qty, shipping, designId,
    setDesignId, setDirty, setTool, setNeedsSizes,
    sizesRef, fabricRef, activeLocationIdRef, locationStatesRef,
    printGuideRef, printLabelRef, pieceThumbnailsRef,
    stripGuidesFromJSON, purgeStrayGuides, persist, buildState, apiSrc,
    getAccessToken, navigate,
  })
  // ── Automated wizard completion ────────────────────────────────────────
  // Applies all choices from the guided flow to the live canvas:
  // colour, sizes, logo image, and a placement preset.
  function handleAutomatedComplete(result: AutomatedDesignResult) {
    setColourId(result.colourId)
    setQty(result.sizes)

    const isDoubleSided = result.placement === 'front-and-back'
    let backLocId: string | undefined
    if (isDoubleSided && product) {
      const backLoc = product.printLocations?.find((l) =>
        l.name.trim().toLowerCase().includes('back'),
      )
      if (backLoc) {
        backLocId = backLoc.id
        setRevealedLocationIds((prev) => new Set([...prev, backLoc.id]))
      }
    }

    const logoUpload = {
      id: result.logoFile.id,
      originalName: result.logoFile.originalName,
      url: result.logoFile.url,
      mimeType: result.logoFile.mimeType ?? 'image/png',
      size: result.logoFile.size ?? 0,
      widthPx: result.logoFile.widthPx,
      heightPx: result.logoFile.heightPx,
      createdAt: new Date().toISOString(),
    }

    // Helper: apply the placement preset using an explicit zone so it works
    // correctly even inside setTimeout callbacks where activeLocation state
    // may still point to the previous location.
    function applyPlacementNow(zone: { left: number; top: number; width: number; height: number }) {
      const c = fabricRef.current; if (!c) return
      const guide = printGuideRef.current; const label = printLabelRef.current
      const pos = result.frontPosition
      if (pos === 'large-center' || pos === 'large-center-top') {
        // Call fitPlacement/centerDelta directly with the explicit zone instead
        // of placementFit/placementCenter which use stale activeLocation state.
        const bbox = userBoundingBox(); if (!bbox) return
        const { factor, targetLeft, targetTop: fitTop } = fitPlacement('contain', zone, bbox)
        // large-center-top: align artwork to the top edge of the print zone
        const targetTop = pos === 'large-center-top'
          ? zone.top
          : fitTop
        for (const o of userObjects()) {
          const relX = (o.left ?? 0) - bbox.left
          const relY = (o.top  ?? 0) - bbox.top
          o.set({ scaleX: (o.scaleX ?? 1) * factor, scaleY: (o.scaleY ?? 1) * factor,
                  left: targetLeft + relX * factor, top: targetTop + relY * factor })
          o.setCoords()
        }
        c.requestRenderAll(); markDirty()
        // For large-center, nudge to exact centre after paint; skip for top-anchored variant.
        if (pos === 'large-center') {
          window.setTimeout(() => {
            const bbox2 = userBoundingBox(); if (!bbox2) return
            const { dx, dy } = centerDelta(zone, bbox2)
            for (const o of userObjects()) {
              o.set({ left: (o.left ?? 0) + dx, top: (o.top ?? 0) + dy }); o.setCoords()
            }
            c.requestRenderAll(); markDirty()
          }, 60)
        }
      } else {
        const userObjs = c.getObjects().filter(
          (o) => o !== guide && o !== label &&
          !(o as fabric.Object & { __mockupBg?: boolean }).__mockupBg,
        )
        const img = userObjs[userObjs.length - 1]; if (!img) return
        const targetW = zone.width * 0.28
        const currentW = img.getScaledWidth(); if (currentW === 0) return
        const factor = targetW / currentW
        img.set({
          scaleX: (img.scaleX ?? 1) * factor, scaleY: (img.scaleY ?? 1) * factor,
          left: pos === 'pocket-left' ? zone.left + 8 : zone.left + zone.width - targetW - 8,
          top: zone.top + 10,
        })
        img.setCoords(); c.requestRenderAll(); markDirty()
      }
    }

    // Clear resume indicator. Reset key is incremented when the wizard is
    // opened fresh (not here) so the Resume button stays gone after completion.
    setWizardProgress(null)

    // Drop logo onto FRONT (currently active location).
    // Guard against the async fromURL callback firing after we've switched
    // to the back location — it must only add to the front canvas.
    const frontLocId = activeLocationIdRef.current ?? undefined
    useUpload(logoUpload, frontLocId)
    setAutoWizardOpen(false)

    // Capture front zone before any async state changes.
    const frontZone = activeLocation ?? product?.printArea ?? PRINT_DEFAULT

    // Cancel any in-flight placement from a previous wizard run.
    placementTimersRef.current.forEach(clearTimeout)
    placementTimersRef.current = []

    const t1 = window.setTimeout(() => {
      applyPlacementNow(frontZone)

      if (!isDoubleSided || !backLocId) return

      // After front placement settles, load the back image in memory, pre-build
      // the back canvas state using the same percentage-based positioning as the
      // wizard preview, store it in locationStatesRef, then switch to back.
      // If already on back (re-run), reload the canvas directly to bypass the
      // switchLocation guard that skips when nextId === activeLocationId.
      const settlePause = result.frontPosition === 'large-center' ? 200 : 100
      const t2 = window.setTimeout(() => {
        const backLoc = product?.printLocations?.find((l) => l.id === backLocId)
        const backZone = backLoc ?? product?.printArea ?? PRINT_DEFAULT
        fabric.Image.fromURL(logoUpload.url, (backImg) => {
          ;(backImg as fabric.Object & { data?: object }).data = { fileId: logoUpload.id, reused: true }
          backImg.scaleToWidth(160)
          const bp = result.backPosition ?? 'large-center'
          const targetW = backZone.width * (bp === 'small-upper-center' ? 0.42 : 0.80)
          const factor = targetW / backImg.getScaledWidth()
          const finalH = backImg.getScaledHeight() * factor
          // large-center: true vertical centre; large-center-top: top of zone; small-upper-center: near top
          const targetTop = bp === 'large-center'
            ? backZone.top + (backZone.height - finalH) / 2
            : bp === 'large-center-top'
              ? backZone.top
              : backZone.top + backZone.height * 0.04
          backImg.set({
            scaleX: (backImg.scaleX ?? 1) * factor,
            scaleY: (backImg.scaleY ?? 1) * factor,
            left: backZone.left + (backZone.width - targetW) / 2,
            top: targetTop,
          })
          backImg.setCoords()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          locationStatesRef.current[backLocId!] = { objects: [backImg.toObject(['data'])] } as any

          // After loadFromJSON completes (either path), nudge artwork to be
          // exactly horizontally centred within backZone using the actual
          // rendered bounding box — eliminates any floating-point drift from
          // the pre-build calculation.
          const nudgeCenterH = () => {
            const c2 = fabricRef.current; if (!c2) return
            const bbox = userBoundingBox(); if (!bbox) return
            const dx = backZone.left + (backZone.width - bbox.width) / 2 - bbox.left
            if (Math.abs(dx) < 0.5) return
            for (const o of userObjects()) { o.set({ left: (o.left ?? 0) + dx }); o.setCoords() }
            c2.requestRenderAll()
            locationStatesRef.current[backLocId!] = stripGuidesFromJSON(
              c2.toJSON() as { objects?: Array<{ type?: string; strokeDashArray?: unknown; text?: string }> },
            )
          }

          if (activeLocationIdRef.current === backLocId) {
            // Already on back — switchLocation would early-return (nextId === activeLocationId).
            // Reload the pre-built state directly so a re-run always applies the new artwork.
            const c = fabricRef.current; if (!c) return
            c.loadFromJSON(locationStatesRef.current[backLocId!], () => {
              if (printGuideRef.current && !c.contains(printGuideRef.current)) c.add(printGuideRef.current)
              if (printLabelRef.current && !c.contains(printLabelRef.current)) c.add(printLabelRef.current)
              c.renderAll()
              nudgeCenterH()
            })
          } else {
            switchLocation(backLocId!)
            // switchLocation's loadFromJSON is async — wait for it to finish
            // before applying the centering correction.
            window.setTimeout(nudgeCenterH, 300)
          }
        }, { crossOrigin: 'anonymous' })
      }, settlePause)
      placementTimersRef.current.push(t2)
    }, 800)
    placementTimersRef.current.push(t1)
  }

  if (error) return <div className="min-h-screen flex flex-col"><TopBar /><div className="p-10 text-ink-3">Error: {error}</div></div>
  if (!catalog || !product) return <div className="min-h-screen flex flex-col"><TopBar title="Garment Designer" /><div className="flex-1 flex items-center justify-center text-ink-3 text-sm">Loading…</div></div>

  const garments = catalog.products.filter((p) => p.kind === 'garment')

  // Customer-side wizard step state. The Designer is panel-based, not
  // scroll-based, so each step's onClick flips the LEFT TOOL NAV state
  // (setTool) or fires openSubmit/etc. Done detection drives the
  // checkmarks on each pill.
  const hasArtwork = (() => {
    const c = fabricRef.current
    if (!c) return false
    // Background image counts as the mockup, not user art — and so do
    // the dashed print-area guide rect + "PRINT AREA" label that we
    // add on canvas init. Filter all three so an untouched canvas
    // correctly reads "no artwork yet".
    const guide = printGuideRef.current
    const label = printLabelRef.current
    return c.getObjects().some((o) => {
      if (o === guide || o === label) return false
      if ((o as fabric.Object & { __mockupBg?: boolean }).__mockupBg) return false
      return true
    })
  })()
  // Clicking a step pill opens a focused popup with the inputs for
  // that step. Submit bypasses the modal — it has its own confirm flow.
  const wizardStepOrder = ['product', 'location', 'art', 'placement', 'sizes'] as const
  type WizStep = typeof wizardStepOrder[number]

  function gotoWizStep(dir: 'next' | 'prev') {
    if (!wizModalStep) return
    const i = wizardStepOrder.indexOf(wizModalStep as WizStep)
    if (i === -1) return
    function open(next: WizStep) {
      // The "art" step uses the dedicated AddDesignModal (Upload + AI)
      // instead of the slim WizardStepModal — keep that consistent
      // when navigating in/out via Prev/Next as well.
      if (next === 'art') { setWizModalStep(null); setDesignChoiceOpen(true); return }
      setWizModalStep(next)
    }
    if (dir === 'next') {
      if (i === wizardStepOrder.length - 1) { setWizModalStep(null); openSubmit(); return }
      open(wizardStepOrder[i + 1])
    } else {
      if (i === 0) { setWizModalStep(null); return }
      open(wizardStepOrder[i - 1])
    }
  }

  return (
    <div className="h-screen flex flex-col bg-surface overflow-hidden">
      <TopBar
        title="Garment Designer"
        right={
          <div className="flex items-center gap-3 text-xs">
            {user && (
              <button
                onClick={() => {
                  if (!designId) { setSaveNameValue(''); setSaveNameOpen(true) }
                  else { void persist() }
                }}
                disabled={!dirty || saving === 'saving'}
                className={'btn-ghost-dark ' + (dirty ? 'text-gold' : 'text-white/45 cursor-default')}
                title={dirty ? (designName ? `Save "${designName}"` : 'Save current design to your library') : 'No unsaved changes'}>
                {saving === 'saving' ? 'Saving…' : saving === 'error' ? 'Retry save' : dirty ? 'Save' : 'Saved'}
              </button>
            )}
            <button onClick={() => setLibraryOpen(true)} className="btn-ghost-dark">My Library</button>
            <div className="hidden sm:flex items-center gap-2 text-white/70"><span className="text-white/45">QTY</span><span className="font-semibold text-white">{totals.totalQty}</span></div>
            <div className="flex items-center gap-2 text-white/70"><span className="text-white/45">$</span><span className="font-semibold text-gold text-base">{totals.total.toFixed(2)}</span></div>
            <button onClick={openSubmit} className="btn-gold">Submit</button>
          </div>
        }
      />

      <button
        type="button"
        onClick={() => { setWizardResetKey((k) => k + 1); setAutoWizardOpen(true) }}
        className="flex-shrink-0 w-full bg-brick hover:bg-brick-dark transition-colors px-6 py-3 flex items-center justify-center gap-3 text-white">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <path d="M12 2l2.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
        <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Click here for automated assistance with your brand apparel</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" className="shrink-0">
          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
        </svg>
      </button>

      <div className="flex flex-1 min-h-0">
        <DesignerToolNav
          tool={tool}
          setTool={setTool}
          setAddDesignOpen={(val: boolean) => { if (val) setDesignChoiceOpen(true) }}
          setStudioMediaOpen={setStudioMediaOpen}
          setLibraryOpen={setLibraryOpen}
          deleteSelected={deleteSelected}
          hasSelection={hasSelection}
        />

        {/* Canvas toolbar — zoom / select-all / border / group / undo / redo. */}
        <div className="w-[140px] flex-shrink-0 border-r border-border bg-white flex flex-col">
          <DesignerToolbar
            zoom={zoom}
            setZoom={setZoom}
            selectAll={selectAll}
            borderOn={borderOn}
            setBorderOn={setBorderOn}
            product={product}
            setPreviewOpen={setPreviewOpen}
            setThreeDOpen={setThreeDOpen}
            groupSelected={groupSelected}
            undo={undo}
            redo={redo}
          />
        </div>

        <main ref={mainRef} className="flex-1 relative overflow-auto flex items-center justify-center bg-[radial-gradient(circle_at_center,#fff_0%,#F9FAFB_60%,#EEEEEE_100%)]">
          <div className="relative" style={{ width: CANVAS_W * zoom, height: CANVAS_H * zoom }}>
            {/* Background subtree — React swaps img↔svg on product/location
                change. Isolated in its own absolute div so reconciliation
                never tries to insertBefore a sibling that Fabric has DOM-
                wrapped. Per-location mockup wins over the product-level one. */}
            <div className="absolute inset-0 pointer-events-none">
              {(() => {
                const url = resolvedMockupUrl
                const errorKey = activeLocation?.id ?? product.id
                const calibStyle = mockupCalibrationCss(activeLocation?.mockupCalibration ?? null)
                if (url && !mockupErrored[errorKey]) {
                  return (
                    <img
                      key={`mock-${errorKey}`}
                      src={url}
                      alt={product.name}
                      className="w-full h-full object-contain"
                      style={calibStyle}
                      onError={() => setMockupErrored((m) => ({ ...m, [errorKey]: true }))}
                    />
                  )
                }
                return (
                  <svg key={`svg-${errorKey}`} viewBox="0 0 400 480" className="w-full h-full" style={{ color: colour?.hex ?? '#222' }}>
                    <path d={silhouettePath(product.slug)} fill="currentColor" />
                  </svg>
                )
              })()}
            </div>
            {/* Canvas subtree — Fabric owns this div + everything inside it.
                React never re-renders this branch (no state-driven children). */}
            <div className="absolute inset-0">
              <canvas ref={canvasElRef} width={CANVAS_W} height={CANVAS_H} />
            </div>
            {/* The size + DPI + delete chip now lives inside the floating
                AlignmentToolbar (rendered at the page level below) so it's
                a single floating section instead of two stacked. */}
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.2em] text-ink-3 font-semibold">{product.name} · {colour?.name ?? '—'}</div>
        </main>

        {/* NOW EDITING + Locations panel — right of canvas, left of sidebar */}
        <div className="w-44 flex-shrink-0 border-l border-border bg-white flex flex-col overflow-hidden">
          {activeLocation && (
            <div className="bg-cream border-b-2 border-brick px-3 py-2.5 shrink-0 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brick text-white shrink-0">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
              </span>
              <div className="leading-tight min-w-0">
                <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-brick">Now editing</div>
                <div className="text-xs font-heading font-bold uppercase text-ink tracking-wide leading-tight truncate">
                  {activeLocation.name}
                  {product?.printLocations && product.printLocations.length > 1 && (
                    <span className="ml-1 text-[10px] font-mono font-semibold text-ink-3 normal-case tracking-normal">
                      {product.printLocations.findIndex((l) => l.id === activeLocation.id) + 1}/{product.printLocations.length}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <DesignerLocations
            product={product}
            colour={colour}
            activeLocationId={activeLocationId}
            revealedLocationIds={revealedLocationIds}
            setRevealedLocationIds={setRevealedLocationIds}
            locationThumbs={locationThumbs}
            setLocationThumbs={setLocationThumbs}
            locationStatesRef={locationStatesRef}
            switchLocation={switchLocation}
            apiSrc={apiSrc}
          />
        </div>

        <DesignerSidebar
          tool={tool}
          garments={garments}
          productSlug={productSlug}
          setProductSlug={setProductSlug}
          product={product}
          colourId={colourId}
          setColourId={setColourId}
          needsSizes={needsSizes}
          setNeedsSizes={setNeedsSizes}
          qty={qty}
          setQty={setQty}
          totals={totals}
          sizesRef={sizesRef}
          addText={addText}
          textDragMode={textDragMode}
          setTextDragMode={setTextDragMode}
          onPickFile={onPickFile}
          hasUser={!!user}
          libraryRef={libraryRef}
          onUseUpload={(u) => useUpload(u, activeLocationIdRef.current ?? undefined)}
          setLibraryOpen={setLibraryOpen}
          onContinue={openSubmit}
        />
      </div>{/* end outer flex row */}

      <AlignmentToolbar
        canvas={fabricRef.current}
        tick={canvasTick}
        bounds={activeLocation ? { left: activeLocation.left, top: activeLocation.top, width: activeLocation.width, height: activeLocation.height } : (product?.printArea ?? PRINT_DEFAULT)}
        sizeInfo={selection}
        stackAboveTextToolbar={(() => {
          const a = fabricRef.current?.getActiveObject()
          return !!(a && (a.type === 'i-text' || a.type === 'textbox' || a.type === 'text'))
        })()}
      />
      <TextToolbar canvas={fabricRef.current} tick={canvasTick} />
      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        canvas={fabricRef.current}
        productId={product.id}
        locationId={activeLocation?.id ?? null}
        garmentName={product.name}
        locationName={activeLocation?.name}
        aiReferenceUrl={activeLocation?.aiReferenceUrl ? apiSrc(activeLocation.aiReferenceUrl) : null}
        aiReferenceBox={activeLocation?.aiReferenceBox ?? null}
      />
      <ThreeDPreviewModal
        open={threeDOpen}
        onClose={() => setThreeDOpen(false)}
        canvas={fabricRef.current}
        // Slug drives the GLB lookup. When `apps/web/public/garments/<slug>.glb`
        // exists the modal uses the real 3D model + decal projection;
        // otherwise it falls back to the curved-plane composite.
        garmentSlug={product.slug}
        mockupUrl={
          // Per-location mockup wins over the product-level one, matching
          // the 2D Designer view. Run through apiSrc so the signed-URL
          // token is attached for the cross-fetch into the offscreen canvas.
          (resolvedMockupUrl ? apiSrc(resolvedMockupUrl) : null)
        }
        fallbackColorHex={colour?.hex ?? null}
        garmentName={product.name}
        locationName={activeLocation?.name}
      />

      <UploadConsentModal
        open={!!pendingUpload}
        pending={pendingUpload}
        warnings={pendingWarnings}
        onConfirm={() => {
          if (pendingUpload) { rememberSessionConsent(); void doUpload(pendingUpload) }
          setPendingUpload(null); setPendingWarnings([])
        }}
        onCancel={() => { setPendingUpload(null); setPendingWarnings([]) }}
      />
      <StudioMediaPicker
        open={studioMediaOpen}
        onClose={() => setStudioMediaOpen(false)}
        onPick={(a: StudioAsset) => useUpload({ id: a.id, originalName: a.originalName, url: a.url, mimeType: a.mimeType, size: 0, widthPx: a.widthPx, heightPx: a.heightPx, createdAt: a.createdAt }, activeLocationIdRef.current ?? undefined)}
      />
      <LibraryDrawer
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        kind="garment"
        onOpenDesign={openSavedDesign}
        onUseUpload={(u) => useUpload(u, activeLocationIdRef.current ?? undefined)}
      />

      {saveNameOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={() => setSaveNameOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow !mb-0">My Library</div>
            <h3 className="font-heading font-bold text-lg text-ink mb-1">Name your design</h3>
            <p className="text-sm text-ink-3 mb-4">Give this design a name so you can find it later in My Library.</p>
            <input
              autoFocus
              type="text"
              value={saveNameValue}
              onChange={(e) => setSaveNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && saveNameValue.trim()) { setSaveNameOpen(false); void persist(saveNameValue.trim()) }
                if (e.key === 'Escape') setSaveNameOpen(false)
              }}
              placeholder="e.g. My Summit Hoodie"
              className="w-full border border-border rounded px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold mb-4"
              maxLength={120}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setSaveNameOpen(false)} className="btn-ghost text-sm px-4">Cancel</button>
              <button
                disabled={!saveNameValue.trim()}
                onClick={() => { setSaveNameOpen(false); void persist(saveNameValue.trim()) }}
                className="btn-primary text-sm px-4 disabled:opacity-40 disabled:cursor-not-allowed">
                Save Design
              </button>
            </div>
          </div>
        </div>
      )}

      <SubmitOrderModal
        submit={submit}
        setSubmit={setSubmit}
        shipping={shipping}
        setShipping={setShipping}
        placeOrder={placeOrder}
        product={product}
        colour={colour}
        totals={totals}
        hasUser={!!user}
      />

      {/* ─────────────────── Wizard step popup ───────────────────
          Triggered when a user clicks a step pill in the brand-red
          EditorWizardBar at the top of the page. Each step renders
          a focused, slim version of the relevant inputs so basic
          users don't have to hunt for them in the side panels. */}
      <DesignerWizardModals
        wizModalStep={wizModalStep}
        setWizModalStep={setWizModalStep}
        gotoWizStep={gotoWizStep}
        product={product}
        garments={garments}
        productSlug={productSlug}
        setProductSlug={setProductSlug}
        colourId={colourId}
        setColourId={setColourId}
        activeLocationId={activeLocationId}
        activeLocation={activeLocation}
        switchLocation={switchLocation}
        hasArtwork={hasArtwork}
        placementCenter={placementCenter}
        placementFit={placementFit}
        placementScale={placementScale}
        qty={qty}
        setQty={setQty}
        setNeedsSizes={setNeedsSizes}
        totals={totals}
      />

      {/* Choice modal — shown when "Add Design" is triggered. Customer
          picks Automated (guided multi-step wizard) or Manual (existing
          upload + AI tabs in AddDesignModal). */}
      <AddDesignChoiceModal
        open={designChoiceOpen}
        onClose={() => setDesignChoiceOpen(false)}
        onChooseAutomated={() => { setDesignChoiceOpen(false); setWizardResetKey((k) => k + 1); setAutoWizardOpen(true) }}
        onChooseManual={(slug: string) => { setProductSlug(slug); setDesignChoiceOpen(false); setAddDesignOpen(true) }}
        garments={garments}
      />

      {/* Automated wizard — guides the customer through colour → logo →
          placement → sizes → review, then fires handleAutomatedComplete
          which applies all choices to the live canvas. */}
      {product && catalog && (
        <AutomatedDesignWizard
          open={autoWizardOpen}
          onClose={() => setAutoWizardOpen(false)}
          product={product}
          catalog={catalog}
          onComplete={handleAutomatedComplete}
          resetKey={wizardResetKey}
          onStepChange={handleWizardStepChange}
          onChooseManual={(partial: ManualChoiceResult) => {
            if (partial.colourId) setColourId(partial.colourId)
            setAutoWizardOpen(false)
            setAddDesignOpen(true)
          }}
        />
      )}

      {/* Floating resume button — appears when the wizard was dismissed mid-flow */}
      {!autoWizardOpen && wizardProgress && (
        <div className="fixed bottom-6 right-6 z-50">
          {/* Close — cancels the in-progress wizard session */}
          <button
            onClick={() => setWizardProgress(null)}
            className="absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full bg-white border border-border shadow flex items-center justify-center text-ink-3 hover:text-ink hover:border-ink-3 transition-colors z-10"
            title="Cancel assisted design session"
            aria-label="Cancel">
            <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/>
            </svg>
          </button>
          <button
            onClick={() => setAutoWizardOpen(true)}
            className="flex items-center gap-3 bg-surface-2 border border-gold/40 hover:border-gold rounded-2xl px-4 py-3 shadow-xl transition-colors group"
          >
            <span className="text-gold shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
              </svg>
            </span>
            <div className="text-left">
              <div className="text-[11px] text-ink-3 leading-none mb-1">
                Step {wizardProgress.current} of {wizardProgress.total}
              </div>
              <div className="text-sm font-semibold text-ink-1 leading-none group-hover:text-gold transition-colors">
                Resume — {wizardProgress.stepName}
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" className="text-ink-3 group-hover:text-gold shrink-0 transition-colors">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </button>
        </div>
      )}

      {/* Add-Design popup — Upload tab + Generate-with-AI tab. Opens
          from the left-nav "Add Design" button AND from the wizard
          "Add artwork" step pill / Prev-Next navigation. AI tab posts
          to /api/files/generate-design (FAL Nano Banana 2, 4K).
          When the file lands, drop it on the canvas via useUpload(),
          which records it in the library + handles positioning. */}
      <AddDesignModal
        open={addDesignOpen}
        onClose={() => setAddDesignOpen(false)}
        onPickFile={onPickFile}
        onUseGeneratedFile={(f: GeneratedFile) => {
          useUpload({
            id: f.id,
            originalName: f.originalName,
            url: f.url,
            mimeType: f.mimeType,
            size: f.size,
            widthPx: f.widthPx,
            heightPx: f.heightPx,
            createdAt: new Date().toISOString(),
          })
        }}
        garmentName={product?.name}
        garmentColourHex={colour?.hex}
        garmentColourName={colour?.name}
      />
    </div>
  )
}

